import { useApolloClient } from '@apollo/react-hooks';
import { ScheduleOfEventsModal } from '../../libs/component-event-modal';
import { Body1, Caption } from '@xo-union/tk-ui-typography';
import ApolloClient from 'apollo-client';
import React, { RefObject } from 'react';
import Icon from '@xo-union/tk-component-icons';
import { EventsQuery, useEventsQuery, useWeddingWebsiteQuery, WeddingWebsiteQuery } from '../../generated/graphql';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import { AnalyticsBaseProps, TrackingContext } from '../../TrackingContext';
import { weddingWebsiteQuery } from '../Dashboard';
import EventCard, { EventType } from './EventCard';
import styles from './eventList.scss';
import { AutoFocusableFields, sanitizeEventWithQuestions, SuccessResponse } from './helper';
import { useEventModalReducer } from './useEventModal';
import useIsLiteSite from '../../helpers/useIsLiteSite';
import LinkButton from '../LinkButton';
import { eventsQuery } from './graphql';
import { useFlipper } from '../../hooks/useFlipper';
import { useLocation } from 'react-router-dom';
import { EventBody } from '../../libs/gds-api-client';

export type TypesOfEvents = 'Ceremony' | 'Reception' | 'WeddingEvent' | 'WeddingDay';

export type Event = EventType & { requireRsvp?: boolean };

interface Props {
  hideTitle?: boolean;
  analyticsProps?: AnalyticsBaseProps;
  iframeRef?: RefObject<HTMLIFrameElement>;
  onEventUpdate?: () => void;
}

const EVENT_RR_EXP_CONTROL = 'control';
const EVENT_RR_EXP_VARIANT = 'variant';

export const onSuccess = (
  event: SuccessResponse,
  client: ApolloClient<object>,
  refetch: () => void,
  iframeRef?: RefObject<HTMLIFrameElement>,
  onEventUpdate?: () => void
) => {
  if (iframeRef) {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({
        type: 'reload',
      }),
      getTargetOrigin()
    );
  }

  if (event.deleted) {
    const eventsCache = client.readQuery<EventsQuery>({
      query: eventsQuery,
    });

    client.writeQuery({
      query: eventsQuery,
      data: {
        events: eventsCache?.events?.filter(e => e?.id !== event.id),
      },
    });
  } else {
    // refetch when adding/updating events because we don't want to reimplement complex event sorting logic
    refetch();
  }

  // call onEventUpdate prop
  if (onEventUpdate) {
    onEventUpdate();
  }
};

export const onHideDate = (hideDate: boolean, client: ApolloClient<object>) => {
  const weddingWebsite = client.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;

  client.writeQuery({
    query: weddingWebsiteQuery,
    data: {
      ...weddingWebsite,
      weddingWebsite: {
        ...weddingWebsite.weddingWebsite,
        hideDate,
      },
    },
  });
};

const EventList: React.FC<Props> = ({ analyticsProps, iframeRef, hideTitle, onEventUpdate = () => {} }) => {
  const client = useApolloClient();
  const { data, refetch } = useEventsQuery();
  const { data: weddingData } = useWeddingWebsiteQuery();
  const { open, eventProps, dispatchOpen, dispatchClose } = useEventModalReducer();
  const events = data?.events || [];
  const isLiteSite = useIsLiteSite();
  const { track } = React.useContext(TrackingContext);
  const { pathname } = useLocation();
  const preview = pathname.includes('preview');

  const { variant } = useFlipper(
    process.env.REACT_APP_FLIPPER_EVENT_RAIL_VENUES_LINK_EXP_ID as string,
    EVENT_RR_EXP_CONTROL
  );
  const isExperiment = variant === EVENT_RR_EXP_VARIANT;

  const onEventClick = (event: Event, autoFocusField: AutoFocusableFields | undefined) => {
    const { __typename, ...eventBody } = event;
    dispatchOpen({
      type: (event.type as TypesOfEvents) || 'WeddingEvent',
      event: sanitizeEventWithQuestions(eventBody),
      autoFocusField: autoFocusField,
    });
    track('WWS Dashboard Interaction', {
      action: 'event update initiated',
    });
  };

  const addNewEvent = () => {
    dispatchOpen({
      type: 'WeddingEvent',
      event: null,
    });
    track('WWS Dashboard Interaction', {
      action: 'add event initiated',
    });
  };

  return (
    <div className={styles.eventContainer}>
      <ScheduleOfEventsModal
        open={open}
        weddingId={weddingData?.guestWedding?.id || ''}
        setClose={() => dispatchClose()}
        hasWWS
        onSuccess={e => onSuccess(e, client, refetch, iframeRef, onEventUpdate)}
        hideDate={isLiteSite ? undefined : weddingData?.weddingWebsite.hideDate}
        onHideDate={hideDate => onHideDate(hideDate, client)}
        {...eventProps}
        baseAnalyticsProps={analyticsProps as any}
        onEscapeKey={dispatchClose}
        isExperiment={isExperiment}
        overlay={!preview}
        preview={preview}
        event={eventProps.event as EventBody | null}
      />
      {preview ? (
        hideTitle ? null : (
          <Body1 bold className={styles.eventTitle}>
            Events
          </Body1>
        )
      ) : (
        <Caption className={styles.eventCaption}>Events</Caption>
      )}
      <div data-testid="event-wrapper" className={styles.eventsWrapper}>
        {events.map((event, index) => {
          if (!event) {
            return null;
          }
          return <EventCard key={event.id} event={event} onClick={onEventClick} />;
        })}
        <LinkButton
          data-testid="add-event-button"
          onClick={addNewEvent}
          className={`${styles.baseTile} ${styles.addEventButton}`}
          color="primary"
        >
          <div className={styles.addEventButtonBody}>
            <Icon size="md" name="add_circle" />
            <span>Add Event</span>
          </div>
        </LinkButton>
      </div>
    </div>
  );
};

export default EventList;
